import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const Nordica = () => {
  const navigate = useNavigate();

  // Prüfen ob der Benutzer authentifiziert ist
  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('portfolioAuth');
    if (!isAuthenticated) {
      // Zurück zur Startseite, wenn nicht authentifiziert
      navigate('/');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-white">
      {/* Decorative Background Elements */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      {/* Header mit Zurück-Button */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <Button 
            onClick={() => navigate('/portfolio')}
            variant="ghost"
            className="text-primary-dark hover:text-accent-green"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Zurück zum Portfolio
          </Button>
        </div>
      </header>

      {/* Überschrift Section mit weißem Hintergrund */}
      <section className="pt-32 pb-12 px-6 bg-white">
        <div className="container mx-auto max-w-6xl text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#0A3A2E' }}>
            Nordica Coffee
          </h1>
          <p className="text-xl sm:text-2xl text-gray-700 mb-8 font-medium">
            Brand redesign | Landing Page | Omnichannel Strategy
          </p>
          <Button 
            size="lg"
            className="text-white font-semibold px-8 py-3 text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
            style={{ backgroundColor: '#0A3A2E' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#072619'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0A3A2E'}
          >
            Zu Nordica
          </Button>
        </div>
      </section>

      {/* Nordica Coffee Store Image Section */}
      <section className="relative overflow-hidden">
        <img 
          src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/gl1em2hw_Bildschirmfoto%202026-02-17%20um%2017.43.53.png"
          alt="Nordica Coffee Store"
          className="w-full h-auto object-cover"
        />
      </section>

      {/* Content Section - Placeholder */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          {/* Hauptüberschrift */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#0A3A2E' }}>
              Nordica Coffee – Der erste echte Hamburger Bio-Kaffee
            </h2>
            <p className="text-2xl text-gray-800 font-semibold mb-8">
              Kaffee ist mehr als ein Getränk. Kaffee ist ein Lebensgefühl.
            </p>
          </div>

          {/* Einleitung */}
          <div className="mb-12 space-y-6 text-lg text-gray-700 leading-relaxed">
            <p>
              Nordica Coffee verbindet das Beste aus zwei Welten: die nordische Klarheit und Effizienz Hamburgs mit der Leidenschaft für außergewöhnlichen Kaffee und nachhaltiges Handeln.
            </p>
            <p>
              Unsere hochwertigen Bio-Kaffeebohnen stammen aus Fair-Trade-zertifizierten Anbaugebieten in Brasilien, Peru und Indonesien. Dort werden sie unter fairen Bedingungen angebaut und sorgfältig ausgewählt, bevor sie ihren Weg nach Hamburg finden.
            </p>
            <p>
              In Hamburg erhält Nordica seinen einzigartigen Charakter. Durch ein besonders schonendes Langzeitröstverfahren entwickeln die Bohnen ihr volles Aroma und ihre ausgewogene Geschmacksstruktur. Anschließend wird der Kaffee direkt vor Ort abgefüllt. So entsteht einer der ersten konsequent in Hamburg veredelten Bio-Kaffees – authentisch, nachhaltig und unverwechselbar norddeutsch.
            </p>
            <p>
              Nordica steht für Qualität ohne Kompromisse. Das ausgezeichnete Preis-Leistungs-Verhältnis macht Premium-Bio-Kaffee für eine breite Zielgruppe zugänglich. Diese Philosophie wurde 2024 mit einer renommierten Auszeichnung gewürdigt.
            </p>
          </div>

          {/* Das neue Nordica */}
          <div className="mb-12 bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-3xl font-bold mb-6" style={{ color: '#0A3A2E' }}>
              Das neue Nordica – Hamburger Lebensgefühl neu interpretiert
            </h3>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p>
                Im Rahmen eines umfassenden Brand-Relaunches wurde die Marke konsequent weiterentwickelt. Das neue Erscheinungsbild verbindet die Gemütlichkeit, Authentizität und Gelassenheit Hamburgs mit einem modernen Bewusstsein für Nachhaltigkeit und Verantwortung.
              </p>
              <p>
                Die neue Landingpage transportiert genau dieses Lebensgefühl: klare Strukturen, warme Bildwelten, nordische Ästhetik und eine konsequente Ausrichtung auf Transparenz, Qualität und Umweltbewusstsein.
              </p>
            </div>
          </div>

          {/* Landing Page Screenshots */}
          <div className="mb-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/reut0qha_Bildschirmfoto%202026-02-24%20um%2011.05.26.png"
              alt="Nordica Landing Page - Dein täglicher Impact-Kaffee"
              className="w-full h-auto rounded-xl shadow-lg"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/lcdy207j_Bildschirmfoto%202026-02-24%20um%2011.06.08.png"
              alt="Nordica Landing Page - Rewards"
              className="w-full h-auto rounded-xl shadow-lg"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/s17hwwt6_Bildschirmfoto%202026-02-24%20um%2011.06.48.png"
              alt="Nordica Landing Page - Unsere Bestseller"
              className="w-full h-auto rounded-xl shadow-lg"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/tfx1bxr3_Bildschirmfoto%202026-02-24%20um%2011.07.06.png"
              alt="Nordica Landing Page - Warum Nordica"
              className="w-full h-auto rounded-xl shadow-lg"
            />
          </div>

          {/* Nachhaltigkeit */}
          <div className="mb-12 bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-3xl font-bold mb-6" style={{ color: '#0A3A2E' }}>
              Nachhaltigkeit, die sichtbar wird
            </h3>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed mb-8">
              <p>
                Mit dem Nordica Club werden Kunden aktiv Teil der Marke.
              </p>
              <p>
                Mitglieder profitieren von exklusiven Rabatten, Sonderaktionen und besonderen Angeboten. Gleichzeitig wird nachhaltiger Konsum unmittelbar belohnt:
              </p>
              <p>
                Sobald ein bestimmter Einkaufswert erreicht wird, finanziert Nordica die Pflanzung eines Baumes. Jedes Clubmitglied kann jederzeit nachvollziehen, wie viele Bäume durch seine Einkäufe bereits gepflanzt wurden und welchen persönlichen Beitrag es zum Umweltschutz leistet.
              </p>
              <p className="font-semibold text-gray-800">
                So entsteht eine echte Verbindung zwischen Genuss und Verantwortung.
              </p>
            </div>

            {/* Marketing & Strategy Images */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/p5nnaoq8_Bildschirmfoto%202026-06-15%20um%2016.49.21.png"
                alt="Deutliche Markt-Vorteile Deutschland"
                className="w-full h-auto rounded-xl shadow-lg"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/ycow4zjm_Bildschirmfoto%202026-06-15%20um%2016.50.13.png"
                alt="Landing Page Interaktion"
                className="w-full h-auto rounded-xl shadow-lg"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/xftdccix_Bildschirmfoto%202026-06-15%20um%2016.53.30.png"
                alt="Instagram-Vorteile"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>

          {/* Digitale Reichweite */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-3xl font-bold mb-6" style={{ color: '#0A3A2E' }}>
              Digitale Reichweite mit klarer Strategie
            </h3>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed mb-8">
              <p>
                Um die Marke langfristig zu stärken, wird die digitale Kommunikation gezielt ausgebaut.
              </p>
              <p>
                Auf Instagram werden hochwertige Inhalte rund um Kaffee, Nachhaltigkeit und Hamburger Lebensart veröffentlicht. Ergänzend sorgen emotionale Video-Kampagnen auf YouTube für eine stärkere Markenbekanntheit und Reichweite.
              </p>
              <p>
                Parallel dazu entsteht ein redaktioneller Blog zu den Themen Kaffee, Nachhaltigkeit, Fair Trade und Umweltbewusstsein. Die Inhalte werden konsequent nach den aktuellen Anforderungen von Google AI, GEO (Generative Engine Optimization) und modernen Suchmaschinenstrategien entwickelt.
              </p>
              <p className="font-semibold text-gray-800">
                Damit wird Nordica nicht nur für Menschen sichtbar, sondern auch für die KI-gestützten Suchsysteme der Zukunft.
              </p>
            </div>

            {/* Blog Content Images */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/wefmi2qg_Bildschirmfoto%202026-06-15%20um%2020.12.52.png"
                alt="Nordica Blog - Precision Roasted & Coffee as Daily Ritual"
                className="w-full h-auto rounded-xl shadow-lg"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/48g7dgek_Bildschirmfoto%202026-06-15%20um%2020.13.05.png"
                alt="Nordica Blog - Hamburg Harbor Craft & Coffee Culture"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-6" style={{ backgroundColor: '#0A3A2E' }}>
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Mehr Projekte entdecken?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Entdecken Sie weitere spannende Projekte aus meinem Portfolio.
          </p>
          <Button 
            onClick={() => navigate('/portfolio')}
            size="lg"
            className="bg-white hover:bg-gray-100 font-semibold px-8 py-3 text-lg"
            style={{ color: '#0A3A2E' }}
          >
            Zurück zum Portfolio
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Nordica;
